import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { atListMyRequests, atCheckSession } from "@/lib/api";

const NAVY = "#0A1628";
const GOLD = "#D4AF37";
const GREEN = "#1A7A4A";
const RED = "#A32D2D";

const STATUS_COLOR = {
  open: "#1A7A4A", filled: NAVY, cancelled: "#5b5e69", expired: "#9ca3af",
};

/** Contractor's list of their own labor requests + status pills + match counts. */
export default function AvailableTodayMyRequestsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [session, setSession] = useState(null);

  const load = async () => {
    setLoading(true); setError(null);
    try {
      const r = await atListMyRequests();
      setItems(r.items || []);
    } catch (e) {
      if (e?.response?.status === 401) {
        setSession({ access: false });
      } else {
        setError(e?.response?.data?.detail || e?.message || "Failed to load.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    atCheckSession().then(setSession).catch(() => setSession({ access: false }));
    load();
  }, []);

  if (session && !session.access) {
    return (
      <div style={pageWrap} data-testid="my-requests-signin-required">
        <div style={{ maxWidth: 480, margin: "80px auto", textAlign: "center" }}>
          <h1 style={h1}>SIGN IN TO SEE YOUR REQUESTS</h1>
          <p style={{ color: "#5b5e69" }}>Available Today labor requests are included on $149+ plans. Sign in from the browse page with the email on your subscription.</p>
          <Link to="/available-today" style={{ ...btnPrimary, display: "inline-block", textDecoration: "none", marginTop: 20 }}>
            ← BACK TO BROWSE
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div style={pageWrap} data-testid="my-requests-page">
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start", gap: 20, flexWrap: "wrap", marginBottom: 22 }}>
          <div>
            <div style={{ color: GOLD, letterSpacing: 3, fontSize: 12, fontWeight: 700 }}>AVAILABLE TODAY · MY LABOR REQUESTS</div>
            <h1 style={h1}>YOUR OPEN & CLOSED REQUESTS</h1>
          </div>
          <Link to="/available-today/requests/new" data-testid="new-request-cta" style={btnPrimary}>
            + POST A NEW REQUEST
          </Link>
        </div>

        {loading && <div style={{ padding: 40, textAlign: "center", color: "#5b5e69" }}>Loading…</div>}
        {error && <div style={errBox}>{error}</div>}
        {!loading && items.length === 0 && (
          <div style={{ padding: 60, textAlign: "center", background: "#fff", borderRadius: 14, border: "1px solid #e5e7ee" }} data-testid="no-requests">
            <p style={{ color: "#5b5e69", marginBottom: 20 }}>You haven&apos;t posted any labor requests yet. Post your first one and get SMS-notified workers within minutes.</p>
            <Link to="/available-today/requests/new" data-testid="first-request-cta" style={btnPrimary}>
              + POST YOUR FIRST REQUEST
            </Link>
          </div>
        )}

        <div style={grid} data-testid="request-list">
          {items.map((r) => (
            <Link key={r.id} to={`/available-today/requests/${r.id}`}
                  data-testid={`request-card-${r.id}`}
                  style={{ ...cardStyle, textDecoration: "none", color: NAVY, cursor: "pointer" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                <div>
                  <div style={{ fontFamily: "Bebas Neue, Arial, sans-serif", fontSize: 20, letterSpacing: 1 }}>{(r.skills || []).join(", ")}</div>
                  <div style={{ color: "#5b5e69", fontSize: 12 }}>
                    {r.city}{r.state ? `, ${r.state}` : ""} · {r.workers_needed} worker{r.workers_needed > 1 ? "s" : ""} · ${r.hourly_offered_usd}/hr
                  </div>
                </div>
                <span style={{ background: STATUS_COLOR[r.status] || "#5b5e69", color: "#fff", padding: "4px 10px", borderRadius: 12, fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase" }} data-testid={`request-status-${r.id}`}>
                  {r.status}
                </span>
              </div>
              <div style={{ marginTop: 10, fontSize: 12, color: "#5b5e69" }}>
                {r.total_matches || 0} matches ·
                {" "}accepted: {r.match_counts?.accepted || 0} ·
                {" "}declined: {r.match_counts?.declined || 0} ·
                {" "}pending: {(r.match_counts?.queued || 0) + (r.match_counts?.notified_sms || 0) + (r.match_counts?.notified_voice || 0)}
              </div>
              <div style={{ marginTop: 6, fontSize: 11, color: "#9ca3af", letterSpacing: 1 }}>
                Posted {new Date(r.created_at).toLocaleString()}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

const pageWrap = { background: "#F8F9FA", minHeight: "100vh", padding: "40px 20px" };
const h1 = { fontFamily: "Bebas Neue, Arial, sans-serif", letterSpacing: 2.5, fontSize: 42, color: NAVY, margin: "6px 0 14px" };
const grid = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: 14 };
const cardStyle = { background: "#fff", padding: "18px 20px", borderRadius: 14, border: "1px solid #e5e7ee", boxShadow: "0 8px 16px -8px rgba(10,22,40,0.08)" };
const btnPrimary = { background: GOLD, color: NAVY, border: "none", padding: "14px 22px", borderRadius: 12, fontWeight: 700, letterSpacing: 1.5, cursor: "pointer", fontSize: 14, fontFamily: "Bebas Neue, Arial, sans-serif", textDecoration: "none", display: "inline-block" };
const errBox = { color: RED, background: "#FDECEC", padding: "12px 14px", borderRadius: 10, marginBottom: 14, fontSize: 14 };
